import { FormEvent, ReactNode, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import Button from 'src/components/Button';
import Select from 'src/components/Select';
import { UnsavedChangesModal } from 'src/components/UnsavedChangesModal';
import { useGetRelatedServiceStatusQuery } from 'src/dictionaries/dictionariesApi';
import { useUnsavedChangesWarning } from 'src/hooks/useUnsavedChangesWarning';
import ChevronRight from 'src/icons/ChevronRight';

import { InvoiceDetails, RelatedService, TRelatedServicesStatus } from '../billing/billing.types';
import { useServiceDateRange } from '../log-coordination/useActivityDatePicker.hook';
import { ServiceAgency } from './agency/ServiceAgency';
import { ServiceAgencyForm } from './agency/ServiceAgencyForm';
import { useServiceAgencyForm } from './agency/ServiceAgencyForm.hook';
import { ServiceIndependent } from './independent/ServiceIndependent';
import { ServiceIndependentForm } from './independent/ServiceIndependentForm';
import { useServiceIndependentForm } from './independent/ServiceIndependentForm.hook';
import { ServiceNotServiced } from './not-services/ServiceNotServiced';
import { ServiceNotServicedForm } from './not-services/ServiceNotServicedForm';
import { useServiceNotServicedForm } from './not-services/ServiceNotServicedForm.hook';

type RelatedServiceCardProps = {
  relatedService: RelatedService;
  invoiceData?: InvoiceDetails;
};

type CardColumnProps = {
  label?: string;
  text?: string;
  children?: ReactNode;
};

type FormConfigItem = {
  formComponent: ReactNode;
  infoComponent: ReactNode;
  methods: any;
  handleSubmit: any;
  isLoading: boolean;
};

export const CardColumn = ({ label, text, children }: CardColumnProps) => (
  <div className="flex flex-1 flex-col gap-1">
    {label && <p className="text-neutral-400 typography-body-sm">{label || ''}</p>}
    {text ? <p className="typography-text-loud text-neutral-100">{text}</p> : children}
  </div>
);

export const RelatedServiceCard = ({ relatedService, invoiceData }: RelatedServiceCardProps) => {
  const { data: relatedServiceStatus } = useGetRelatedServiceStatusQuery();

  const statusOptions =
    relatedServiceStatus?.map((item) => ({
      label: item.value,
      value: item.key as TRelatedServicesStatus,
    })) ?? [];

  const [status, setStatus] = useState<TRelatedServicesStatus>(relatedService.status);

  const [editMode, setEditMode] = useState(false);

  const { showModal, confirmNavigation, cancelNavigation } = useUnsavedChangesWarning(editMode);
  const { isDateDisabled } = useServiceDateRange(invoiceData);

  const {
    methods: agencyMethods,
    handleSubmit: handleAgencySubmit,
    isLoading: isAgencyLoading,
  } = useServiceAgencyForm({
    setEditMode,
    relatedService,
  });
  const {
    methods: independentMethods,
    handleSubmit: handleIndependentSubmit,
    isLoading: isIndependentLoading,
  } = useServiceIndependentForm({
    setEditMode,
    relatedService,
  });
  const {
    methods: notServicedMethods,
    handleSubmit: handleNotServicedSubmit,
    isLoading: isNotServicedLoading,
  } = useServiceNotServicedForm({
    setEditMode,
    relatedService,
  });

  const formConfig: Record<TRelatedServicesStatus, FormConfigItem> = {
    ServicedIndependently: {
      formComponent: (
        <ServiceIndependentForm
          year={relatedService.schoolYearLabel}
          isDateDisabled={isDateDisabled}
        />
      ),
      infoComponent: <ServiceIndependent year={relatedService.schoolYearLabel} />,
      methods: independentMethods,
      handleSubmit: handleIndependentSubmit,
      isLoading: isIndependentLoading,
    },
    ServicedViaAgency: {
      formComponent: (
        <ServiceAgencyForm year={relatedService.schoolYearLabel} isDateDisabled={isDateDisabled} />
      ),
      infoComponent: <ServiceAgency year={relatedService.schoolYearLabel} />,
      methods: agencyMethods,
      handleSubmit: handleAgencySubmit,
      isLoading: isAgencyLoading,
    },
    NotServiced: {
      formComponent: <ServiceNotServicedForm />,
      infoComponent: <ServiceNotServiced />,
      methods: notServicedMethods,
      handleSubmit: handleNotServicedSubmit,
      isLoading: isNotServicedLoading,
    },
  };

  const currentConfig = status ? formConfig[status] : null;

  const renderStatusSelect = () => (
    <CardColumn label="Status">
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value as TRelatedServicesStatus);
            if (!status) setEditMode(true);
          }}
          options={statusOptions}
          placeholder="Select status"
          className="w-40 md:w-full"
        />
        {status && (
          <Button
            size="sm"
            preset="secondary"
            className="ml-auto self-end"
            isLoading={currentConfig?.isLoading}
            type="submit"
          >
            <div className="flex items-center justify-center gap-2">
              Save
              <ChevronRight fontSize="16" />
            </div>
          </Button>
        )}
      </div>
    </CardColumn>
  );

  const renderStatusDisplay = () => (
    <CardColumn>
      <div className="flex items-center justify-between">
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-neutral-400 typography-body-sm">Status</p>
          <p className="typography-text-loud text-neutral-100">
            {statusOptions.find((option) => option.value === status)?.label}
          </p>
        </div>
        <Button
          size="md"
          preset="tertiary"
          className="w-fit self-end"
          type="button"
          onClick={(e) => {
            setEditMode(true);
            e.preventDefault();
          }}
        >
          Edit
        </Button>
      </div>
    </CardColumn>
  );

  const renderContent = () => {
    return editMode ? currentConfig?.formComponent : currentConfig?.infoComponent;
  };

  const formProviderMethods = () => {
    return currentConfig?.methods || {};
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    return currentConfig?.handleSubmit() || null;
  };

  const metricLabel = relatedService.frequency ? 'Frequency' : 'Mandate';
  const metricText = relatedService.frequency
    ? `${relatedService.frequency} x ${relatedService.duration}`
    : relatedService.mandate?.toString();

  return (
    <form onSubmit={handleSubmit}>
      <FormProvider {...formProviderMethods()}>
        <div
          className={`rounded-lg border p-4 ${
            editMode
              ? 'border-secondary-400 bg-secondary-900 '
              : 'border-neutral-800 bg-neutral-white '
          }`}
        >
          <div className="flex flex-row items-start justify-between gap-4 ">
            <CardColumn label="Related service" text={relatedService.serviceType} />
            <CardColumn label={metricLabel} text={metricText} />
            {!status || editMode ? renderStatusSelect() : renderStatusDisplay()}
          </div>
          {renderContent()}
        </div>
      </FormProvider>

      <UnsavedChangesModal
        isOpen={showModal}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
    </form>
  );
};
